import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { mapIdFromPrettyPath } from '../../utils/prettyRoutes';
import { MapImageView } from '../../components/world/MapImageView';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { documentedAccessLevels, mapPath, monsterName, monsterPath, prettyAccessName } from '../../utils/worldFormat';
import type { WorldData, WorldMap } from '../../types/world';
type Presence = 'todo' | 'permanente' | 'temporal';
export function MapDetail() {
  const {
    mapId,
    mapSlug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2715" data-mp="1qf2umviao71j">
      {w => {
      const id = mapSlug ? mapIdFromPrettyPath(pathname) : Number(mapId);
      const m = id === undefined ? undefined : w.maps.get(id);
      if (!m) return <NotFound data-id="element-2716" data-mp="20548kugxo92q" />;
      return <>
            <CanonicalPath to={mapPath(m.id)} data-id="element-2717" data-mp="13gg1uuao488m" />
            <MapView w={w} m={m} data-id="element-2718" data-mp="13hr5mu445fzq" />
          </>;
    }}
    </WorldGate>;
}
function NotFound() {
  return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2719" data-mp="17i1jsu227cpo">
      <p className="text-white" data-id="element-2720" data-mp="1cfte1uq6fax1">No encontramos ese mapa en los archivos.</p>
      <Link to="/guia/mapas" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2721" data-mp="1hfo1foaqtidc">Volver a Mapas y spots</Link>
    </div>;
}
function MapView({
  w,
  m
}: {
  w: WorldData;
  m: WorldMap;
}) {
  const [presence, setPresence] = useState<Presence>('todo');
  useEffect(() => {
    document.title = `${m.name} · Mapas · Guía MU Vanguard`;
  }, [m.name]);
  const spawns = w.idx.spawnsByMap.get(m.id) ?? [];
  const spots = useMemo(() => spawns.filter(s => s.kind === 'spot').sort((a, b) => (w.monsters.get(a.monsterId)?.level ?? 0) - (w.monsters.get(b.monsterId)?.level ?? 0) || a.monsterId - b.monsterId), [spawns, w]);
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const locations = w.idx.locationsByMap.get(m.id) ?? [];
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const eventIds = Array.from(new Set([...spawns, ...locations].map(x => x.eventId).filter((v): v is string => !!v)));
  const levels = documentedAccessLevels(m);
  const showPermanent = presence !== 'temporal';
  const showTemporary = presence !== 'permanente';
  const filters: {
    id: Presence;
    label: string;
  }[] = [{
    id: 'todo',
    label: 'Todo'
  }, {
    id: 'permanente',
    label: 'Permanente'
  }, {
    id: 'temporal',
    label: 'Eventos e invasiones'
  }];
  return <div data-id="element-2722" data-mp="38cxeedbd379l">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: m.name
    }]} eyebrow="Mapa" title={m.name} data-id="element-2723" data-mp="38cfcb020pytr">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2724" data-mp="2yhdm00w0aqpl">
          <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2725" data-mp="33em7aacgbav2">
            {levels.length ? `Acceso desde nivel ${Math.min(...levels)}` : 'Acceso especial'}
          </span>
          <span className="text-sm text-gray-400" data-id="element-2726" data-mp="3w3b1rqj04hxu">{spots.length} spots · {new Set(locations.map(l => l.monsterId)).size} monstruos documentados</span>
          <CopyLinkButton label="Copiar enlace del mapa" data-id="element-2727" data-mp="3m53xo89t9ud9" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2728" data-mp="26p4rv3no2ag6">
        <WorldSection id="accesos" title="Accesos y requisitos" data-id="element-2729" data-mp="26o3ir4q7ny13">
          {m.moveEntries.length ? <ul className="divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-2730" data-mp="2ar9nu91l7j5z">
              {m.moveEntries.map(e => <li key={`${e.name}-${e.gateId ?? ''}`} className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-2731" data-mp="2qzfs0cn0v4dt">
                  <span className="text-white" data-id="element-2732" data-mp="2h7jqerlq8w4u">{prettyAccessName(e.name)}</span>
                  <span className="text-sm tabular-nums text-gray-300" data-id="element-2733" data-mp="2h5gqnfurvomx">{e.minLevel !== undefined ? `Nivel ${e.minLevel}` : 'Nivel no documentado'}</span>
                </li>)}
            </ul> : <p className="text-gray-400" data-id="element-2734" data-mp="34xnko8d0zjj6">Sin accesos por movimiento documentados.</p>}
          {m.notes.length > 0 && <ul className="mt-3 space-y-1.5 text-sm text-gray-400" data-id="element-2735" data-mp="2zwzu1nspl4ph">
              {m.notes.map(n => <li key={n} data-id="element-2736" data-mp="2vvo0x8amm1cj">{n}</li>)}
            </ul>}
          <p className="mt-3 text-sm text-gray-500" data-id="element-2737" data-mp="1oj5tygaae7ve">Cada acceso tiene su propio requisito. Los spots no se asignan a una subzona concreta.</p>
        </WorldSection>

        <div role="group" aria-label="Filtrar presencia" className="flex flex-wrap gap-1.5" data-id="element-2738" data-mp="1kg94c5mbxbhy">
          {filters.map(f => <button key={f.id} type="button" aria-pressed={presence === f.id} onClick={() => setPresence(f.id)} className={`h-11 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${presence === f.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-2739" data-mp="413blh7n3woj">
              {f.label}
            </button>)}
        </div>

        <WorldSection id="imagen" title="Mapa" data-id="element-2740" data-mp="nnj97abuh8we">
          <MapImageView w={w} mapId={m.id} spots={showPermanent ? spots : invasionSpawns} data-id="element-2741" data-mp="rqxq037lxtaw" />
        </WorldSection>

        {showPermanent && <WorldSection id="spots" title="Spots configurados" count={spots.length} data-id="element-2742" data-mp="wp8ya9i97vr0">
            {spots.length ? <ul className="grid gap-2.5" data-id="element-2743" data-mp="2tk5ea2577l8x">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-2744" data-mp="2ygc8wys59abk" />)}</ul> : <p className="text-gray-400" data-id="element-2745" data-mp="5m5bvr497go9">No hay spots configurados en spawns.csv para este mapa.</p>}
          </WorldSection>}

        {showTemporary && <WorldSection id="invasiones" title="Apariciones de invasión" count={invasionSpawns.length} data-id="element-2746" data-mp="enbvwa6439m9">
            {invasionSpawns.length ? <ul className="grid gap-2.5" data-id="element-2747" data-mp="9n7b47y67sq8">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-2748" data-mp="oljbaokr5jm5" />)}</ul> : <p className="text-gray-400" data-id="element-2749" data-mp="oj6i1f9jnmts">No hay apariciones de invasión configuradas en este mapa.</p>}
          </WorldSection>}

        <WorldSection id="monstruos" title="Monstruos presentes" data-id="element-2750" data-mp="xcrz5596lg6d">
          {(showPermanent ? permanent : []).length + (showTemporary ? temporary : []).length === 0 ? <p className="text-gray-400" data-id="element-2751" data-mp="17br70lm4h7sb">Sin presencia documentada en monster_locations.csv.</p> : <ul className="grid gap-2 sm:grid-cols-2" data-id="element-2752" data-mp="12dpe78yw9u3n">
              {[...(showPermanent ? permanent : []), ...(showTemporary ? temporary : [])].map(l => <li key={l.id} data-id="element-2753" data-mp="1lwvccyxz241d">
                  <Link to={monsterPath(l.monsterId)} className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2754" data-mp="1qycaimp2nce9">
                    <span className="flex flex-wrap items-baseline gap-x-2" data-id="element-2755" data-mp="1gyttof4gk6qf">
                      <span className="font-semibold text-white" data-id="element-2756" data-mp="138hzmaobbxwn">{monsterName(w, l.monsterId)}</span>
                      {w.monsters.get(l.monsterId)?.level !== undefined && <span className="text-xs text-gray-500" data-id="element-2757" data-mp="180hrrgy55mio">Nv. monstruo {w.monsters.get(l.monsterId)!.level}</span>}
                    </span>
                    <span className={`mt-0.5 text-sm ${l.kind === 'ordinary' ? 'text-gray-400' : 'text-[#D4AF37]/90'}`} data-id="element-2758" data-mp="y8vms2cecjod">{l.condition ?? 'Condición no documentada'}</span>
                  </Link>
                </li>)}
            </ul>}
        </WorldSection>

        {eventIds.length > 0 && showTemporary && <WorldSection id="eventos" title="Eventos e invasiones asociados" data-id="element-2759" data-mp="14pfa9u2q5dm2">
            <div className="flex flex-wrap gap-2" data-id="element-2760" data-mp="zqu99sfnepwk">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2761" data-mp="zpap3qqvfupi" />)}</div>
          </WorldSection>}

        <Disclosure title="Configuración técnica del mapa" data-id="element-2762" data-mp="uudkcw0mk7if">
          <StatList rows={[{
          label: 'experience_rate',
          value: m.experienceRate
        }, {
          label: 'item_drop_rate',
          value: m.itemDropRate
        }, {
          label: 'excellent_rate_per_million',
          value: m.excellentRatePerMillion
        }, {
          label: 'ancient_rate_per_million',
          value: m.ancientRatePerMillion
        }, {
          label: 'socket_items_allowed',
          value: m.socketItemsAllowed === undefined ? undefined : m.socketItemsAllowed ? 'Sí' : 'No'
        }, {
          label: 'ID del mapa',
          value: m.id
        }]} data-id="element-2763" data-mp="ps62jpt45x86" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2764" data-mp="3u7o0cxen7ige">Valores tal como figuran en maps.csv. No son probabilidades finales por monstruo.</p>
        </Disclosure>
      </div>
    </div>;
}